import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  GridLegacy as Grid,
} from "@mui/material";
import { DisplayOnlyField } from "components/Forms/DisplayOnlyField";
import { ErrorDisplay } from "components/Forms/ErrorDisplay";
import { useValidatingFormContext } from "hooks/useValidatingForm";
import { Control, Controller, ControllerRenderProps, PathValue } from "react-hook-form";

import { ChemicalDetailInputs } from ".";

type HazardFieldName = keyof Pick<
  ChemicalDetailInputs,
  "physicalHazards" | "healthHazards" | "oregonHazards" | "ifcHazards"
>;

/**
 * One hazard-class checkbox grid on the chemical detail form. In display-only
 * mode it renders the checked hazards as plain text instead of inert-looking
 * checkboxes, matching how the rest of the form's fields degrade.
 */
export function HazardCheckboxSection<TName extends HazardFieldName>({
  name,
  label,
  order,
  hazardLabels,
  control,
  defaultValue,
  onHazardChange,
}: {
  name: TName;
  label: string;
  order: readonly ChemicalDetailInputs[TName][number][];
  hazardLabels: Record<ChemicalDetailInputs[TName][number], string>;
  control: Control<ChemicalDetailInputs>;
  defaultValue: PathValue<ChemicalDetailInputs, TName> | undefined;
  onHazardChange: (
    e: React.ChangeEvent<HTMLInputElement>,
    checked: boolean,
    field: ControllerRenderProps<ChemicalDetailInputs, TName>
  ) => void;
}) {
  const formContext = useValidatingFormContext();
  if (formContext?.metadata?.isDisplayOnly) {
    const selected: readonly string[] = formContext.getValues(name) ?? [];
    const text = order
      .filter((hazard) => selected.includes(hazard))
      .map((hazard) => hazardLabels[hazard])
      .join(", ");
    return <DisplayOnlyField label={label} text={text || "None"} wrapValue />;
  }

  return (
    <FormControl>
      <FormLabel>{label}</FormLabel>
      <FormGroup>
        <Grid container>
          <Controller
            name={name}
            control={control}
            defaultValue={defaultValue}
            render={({ field, fieldState: { error } }) => {
              const selected: readonly string[] = field.value ?? [];
              return (
                <>
                  <Grid item xs={12}>
                    <ErrorDisplay error={error} />
                  </Grid>
                  {order.map((hazard) => (
                    <Grid key={hazard} item xs={12} sm={6}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            {...field}
                            value={hazard}
                            checked={selected.includes(hazard)}
                            onChange={(e, checked) => onHazardChange(e, checked, field)}
                          />
                        }
                        label={hazardLabels[hazard]}
                      />
                    </Grid>
                  ))}
                </>
              );
            }}
          />
        </Grid>
      </FormGroup>
    </FormControl>
  );
}
